import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import intlTelInput, { Iti } from 'intl-tel-input';
import { BaseInputFieldComponent } from '../base/base-input-field.component';
import { InputDisplay } from '../input-group/input-group.properties';
import { InputFieldType } from '../input-field/input-field.properties';
import { Subject, takeUntil } from 'rxjs';
import { ItiPhoneNumberValidator } from './phone-input.validators';

@Component({
  selector: 'shared-ui-inputs-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.css'],
  standalone: false,
})
export class PhoneInputComponent extends BaseInputFieldComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() separateDialCode = true;
  @Input() formatOnDisplay = true;
  @Input() formatAsYouType = true;
  @Input() nationalMode = true;
  @Input() countrySearch = true;
  @Input() strictMode = true;
  @Input() display = InputDisplay.Block;
  @Input() prependElements: TemplateRef<unknown>[] = [];
  @Input() appendElements: TemplateRef<unknown>[] = [];
  @ViewChild('phoneInput') inputRef?: ElementRef;
  InputFieldType = InputFieldType;
  iti: Iti | null = null;

  private destroy$ = new Subject<void>();
  private isUpdatingFromControl = false;
  private isUpdatingFromInput = false;

  constructor(private cdr: ChangeDetectorRef) {
    super();
  }

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.initializeIntlTelInput();

    if (this.iti) {
      this.control.addValidators(ItiPhoneNumberValidator(this.iti));

      if (this.control.value) {
        this.isUpdatingFromControl = true;
        this.iti.setNumber(this.control.value);
        const fullNumber = this.iti.getNumber();
        if (fullNumber !== this.control.value) {
          this.control.setValue(fullNumber, { emitEvent: false });
        }
        this.isUpdatingFromControl = false;
      }

      this.control.updateValueAndValidity({ emitEvent: false });
    }

    this.inputRef?.nativeElement.addEventListener('input', this.onPhoneInput.bind(this));
    this.inputRef?.nativeElement.addEventListener('countrychange', this.onCountryChange.bind(this));

    this.control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (!this.isUpdatingFromInput && this.iti) {
        this.isUpdatingFromControl = true;
        const currentNumber = this.iti.getNumber();
        if (value !== currentNumber) {
          this.iti.setNumber(value || '');
        }
        this.isUpdatingFromControl = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.iti?.destroy();
  }

  getNumber(): string | null {
    return this.iti?.getNumber() ?? null;
  }

  isNumberValid(): boolean {
    if (this.strictMode) {
      return this.iti?.isValidNumberPrecise() ?? false;
    }
    return this.iti?.isValidNumber() ?? false;
  }

  initializeIntlTelInput(): void {
    const input = this.inputRef?.nativeElement;
    if (input) {
      this.iti = intlTelInput(input, {
        initialCountry: 'auto',
        geoIpLookup: (success, failure) => {
          fetch('https://ipapi.co/json')
            .then((res) => res.json())
            .then((data) => success(data.country_code))
            .catch(() => failure());
        },
        separateDialCode: this.separateDialCode,
        strictMode: this.strictMode,
        formatOnDisplay: this.formatOnDisplay,
        formatAsYouType: this.formatAsYouType,
        nationalMode: this.nationalMode,
        utilsScript: './assets/scripts/utils.js',
      });
    }
  }

  private onPhoneInput(): void {
    if (this.isUpdatingFromControl || !this.iti) {
      return;
    }
    this.isUpdatingFromInput = true;
    const fullNumber = this.iti.getNumber();

    this.control.setValue(fullNumber, { emitEvent: true });
    this.control.markAsDirty();
    this.control.markAsTouched();
    this.control.updateValueAndValidity({ emitEvent: true });

    this.isUpdatingFromInput = false;

    this.cdr.detectChanges();
  }

  private onCountryChange(): void {
    this.onPhoneInput();
  }
}
